import { z } from 'zod';

import { embedSchema } from './freeform-component.schema';
import { homeContentItemSchema } from './home-content-item.schema';
import { homeFixtureSchema } from './home-fixture.schema';

export type HomeBodyControl =
	| z.infer<typeof takeoverSchema>
	| z.infer<typeof matchDayCentreTakeoverSchema>
	| z.infer<typeof topStoriesSchema>
	| z.infer<typeof topVideosSchema>
	| z.infer<typeof teamStoriesSchema>
	| z.infer<typeof advertPanelSchema>
	| z.infer<typeof superBannerSchema>
	| z.infer<typeof embedSchema>;

export const takeoverSchema = z
	.object({
		type: z.literal('Takeover'),
		title: z.string(),
		image: z.string(),
		desktopImage: z.string().optional(),
		icon: z.string().optional(),
		link: z
			.object({
				url: z.string(),
				name: z.string().optional(),
				target: z
					.enum(['_blank', '_self', '_parent', '_top'])
					.optional(),
				rel: z.string().optional(),
			})
			.passthrough()
			.optional(),
	})
	.passthrough();

export const matchDayCentreSchema = z
	.object({
		team: z.string(),
		refreshUrl: z.string(),
		pageUrl: z.string(),
		kickOffTimeZone: z.string().optional(),
	})
	.passthrough();

export const matchDayCentreTakeoverSchema = z
	.object({
		type: z.literal('MatchDayCentreTakeover'),
		refreshUrl: z.string().optional(),
		pageUrl: z.string().optional(),
		kickOffTimeZone: z.string().optional(),
		matchDayCentres: z.array(matchDayCentreSchema).optional(),
	})
	.passthrough();

export const topStoriesSchema = z
	.object({
		type: z.literal('TopStories'),
		topStory: homeContentItemSchema,
		otherStories: z.array(homeContentItemSchema).optional(),
		nextFixture: homeFixtureSchema.optional(),
		lastResult: homeFixtureSchema.optional(),
		// Legacy alias — prefer `lastResult` from freeform.
		lastFixture: homeFixtureSchema.optional(),
		moreLink: z.string().optional(),
		moreLabel: z.string().optional(),
		hideWhenFinished: z.boolean().optional(),
		hideAfter: z.number().optional(),
	})
	.passthrough();

export const topVideosSchema = z
	.object({
		type: z.literal('TopVideos'),
		topVideo: homeContentItemSchema,
		otherVideos: z.array(homeContentItemSchema).optional(),
		moreLink: z.string().optional(),
		moreLabel: z.string().optional(),
		advertId: z.string().optional(),
	})
	.passthrough();

export const teamStoriesSchema = z
	.object({
		type: z.literal('TeamStories'),
		heading: z.string(),
		moreLink: z.string().optional(),
		moreLabel: z.string().optional(),
		advertId: z.string().optional(),
		category: z.string().optional(),
		stories: z.array(homeContentItemSchema).optional(),
		nextFixture: homeFixtureSchema.optional(),
		lastResult: homeFixtureSchema.optional(),
		// Legacy alias — prefer `lastResult` from freeform.
		lastFixture: homeFixtureSchema.optional(),
	})
	.passthrough();

export const advertPanelSchema = z
	.object({
		type: z.literal('AdvertPanel'),
		title: z.string().optional(),
		wrapperClassName: z.string().optional(),
		leftAdvertSlotId: z.string().optional(),
		middleAdvertSlotId: z.string().optional(),
		rightAdvertSlotId: z.string().optional(),
	})
	.passthrough();

export const superBannerSchema = z
	.object({
		type: z.literal('SuperBanner'),
		link: z
			.object({
				url: z.string(),
				target: z.string().optional(),
				name: z.string().optional(),
				rel: z.string().optional(),
			})
			.passthrough(),
		subHeading: z.string().optional(),
		image: z
			.object({
				url: z.string(),
				alt: z.string().optional(),
				length: z.number().optional(),
				mimeType: z.string().optional(),
				width: z.number().optional(),
				height: z.number().optional(),
			})
			.passthrough()
			.optional(),
		includeVideoIcon: z.boolean().optional(),
		blueBackground: z.boolean().optional(),
		hideWhenFinished: z.boolean().optional(),
		hideAfter: z.number().optional(),
	})
	.passthrough();

export const homeBodyControlSchema: z.ZodType<HomeBodyControl> = z.union([
	takeoverSchema,
	matchDayCentreTakeoverSchema,
	topStoriesSchema,
	topVideosSchema,
	teamStoriesSchema,
	advertPanelSchema,
	superBannerSchema,
	embedSchema,
]);
